import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    getUserCurrencyBalance,
    getPublicLeagueTemplates,
    getLeagueCurrencySummary,
} from '../DatabaseAPI';
import {
    SeasonApiError,
    isSeasonFeatureDisabled,
    getSeasonAvailableLeagues,
    getSeasonLeagueTemplates,
} from '../api/seasonApi';
import { useBossBattlesFeatureFlag } from '../api/useBossBattlesFeatureFlag';
import { Input, Modal, Pagination } from '../components/ui';
import './LeagueSignUpPage.css';

const ARENAS_PER_PAGE = 5;

/**
 * Every screen this flow can show. Used to validate BOTH forward navigation and the
 * back handler, so a typo'd screen id fails loudly at the click instead of rendering
 * a blank page.
 */
const SCREEN_IDS = ['join', 'configure', 'arena', 'private'];

/**
 * The root of the flow. Back from here leaves the flow entirely (My Teams), so the
 * history stack is empty whenever this screen is showing.
 */
const ROOT_SCREEN = 'join';

/**
 * The FOUR Configure-League options, in spec order
 * (AIDocs/Future/new-league-join-flow.md, Screen 2).
 *
 * Titles and descriptions are quoted verbatim from that spec. Every entry MUST have
 * a matching `case` in handleConfigureContinue() — the default branch throws, so a
 * new card without a destination fails loudly at Continue instead of doing nothing.
 *
 * ALL FOUR ARE LIVE. The former per-card `placeholder` flag (and its dashed
 * border + inline PLACEHOLDER badge) existed only to mark Season-Long Campaign
 * as having no backend; the season backend has since shipped, so the flag was
 * true for nothing and is deleted rather than left dormant.
 */
// ⚠️ THESE DESCRIPTIONS ARE A PRODUCT PROMISE — only name features that exist.
// A user picks a mode from this copy and cannot un-join afterwards, so a feature
// named here that the app cannot perform is a broken promise, not just wording.
// Before adding a capability to any description, confirm there is a live
// endpoint AND a UI flow that reaches it.
const MATCH_TYPES = [
    {
        id: 'dailyDraft',
        title: 'Daily Draft Battle',
        description: '24-hour draft match with instant payouts.',
    },
    {
        id: 'seasonCampaign',
        title: 'Season-Long Campaign',
        // Was "…with rosters and trades." TRADES DO NOT EXIST. The season URL
        // inventory (football/season/urls.py) has no trade, waiver, add, drop,
        // or roster-release route — the only roster mutation is starter/bench
        // replacement via PUT season-teams/<id>/lineup/. Both capabilities named
        // below are backed: weekly lineups by that endpoint, standings by
        // GET season-leagues/<id>/standings/. Do not restore the trade claim
        // until both a backend endpoint and a UI flow ship.
        description: 'Classic season journey with weekly lineups and standings.',
    },
    {
        id: 'bossFight',
        title: 'Boss Fight',
        description: 'Team up to defeat colossal raid bosses.',
        // ROLLOUT-GATED. This card is rendered ONLY when the `mode_boss_battle`
        // flag admits the viewer — see the `visibleMatchTypes` filter below. It
        // stays in this list (rather than being deleted) because the flag can be
        // switched on per environment; the filter, not the list, decides.
        featureFlag: 'mode_boss_battle',
    },
    {
        id: 'createPrivate',
        title: 'Create Private League',
        description: 'Set up a custom league with invite-only access.',
    },
];

/**
 * The default Configure selection, and the fallback when the currently selected
 * card is withdrawn by a rollout flag mid-session.
 *
 * It must be a card that is NEVER flag-gated, or the fallback could itself be
 * hidden and Continue would act on an invisible selection.
 */
const DEFAULT_MATCH_TYPE = 'dailyDraft';

/**
 * Classifies an in-flight request when it settles.
 *
 * Module-level (not a hook) so it is referentially stable and can be used inside
 * `useCallback` bodies without joining their dependency arrays.
 *
 * Shared by ALL THREE live reads on this page — the header Mythril balance, the
 * public-template list, and the per-template entry fees — each with its own
 * request-id ref (the fees keep one id PER TEMPLATE, see templateCostOutcome).
 *
 * @returns {'current'|'superseded'|'unmounted'} 'current' is the ONLY outcome that
 *   may write state. The other two are normal, expected outcomes — not failures.
 */
function requestOutcome(requestId, currentIdRef, isMountedRef) {
    if (!isMountedRef.current) {
        return 'unmounted';
    }
    if (currentIdRef.current !== requestId) {
        return 'superseded';
    }
    return 'current';
}

/**
 * Same classification, for a fee read belonging to ONE template row.
 *
 * The fee reads need PER-TEMPLATE generations, not the single counter the balance
 * and template-list reads use: every row fetches independently and concurrently, so
 * one shared id would mark every row but the last-issued as superseded and no fee
 * would ever render.
 *
 * `issuedIdsRef.current` is a plain `{ [templateId]: requestId }` map holding the
 * NEWEST id issued for each row. A settling response is 'current' only if its own id
 * is still that row's newest. Two ways it stops being newest:
 *   · a later read for the same row was issued (row Retry fee, or arena re-entry), or
 *   · the map was CLEARED on arena entry — after which `map[templateId]` is
 *     `undefined`, which can never equal a real (numeric) id, so every fee read from
 *     a previous visit is correctly classified 'superseded'.
 *
 * WHY THIS IS A SPEND-PROTECTION GUARD, not just tidiness: `templateCosts[id]` drives
 * both the displayed fee AND the JOIN enable/disable gate. Letting an older response
 * land could re-enable JOIN on a stale price/affordability verdict that the newest
 * read had already invalidated — the user would then be charged
 * `league.get_entry_cost()` against a quote nothing live still vouches for.
 */
function templateCostOutcome(templateId, requestId, issuedIdsRef, isMountedRef) {
    // Adapts the per-template map to the single-value `.current` shape requestOutcome
    // expects, so both guards stay one implementation.
    const rowIdRef = { current: issuedIdsRef.current[templateId] };
    return requestOutcome(requestId, rowIdRef, isMountedRef);
}

/**
 * Renders an entry fee under the THREE-STATE money contract.
 *
 * A real number when known, "Free" only when the backend genuinely reports 0, and
 * "—" when unknown. A placeholder number — or `0` standing in for "we didn't load
 * it" — would advertise a price the backend never agreed to: DraftTeamView charges
 * `league.get_entry_cost()` regardless of what this label said.
 */
function formatEntryFee(cost) {
    if (cost === null || cost === undefined) {
        return '—';
    }
    if (cost === 0) {
        return 'Free';
    }
    return `${cost.toLocaleString()} Mythril`;
}

/**
 * Plain Mythril amount — same unknown→"—" rule, but WITHOUT the "Free" wording.
 *
 * Used for balances and shortfalls, where 0 means "you have none" / "you need
 * nothing more", not "this costs nothing".
 */
function formatMythril(amount) {
    if (amount === null || amount === undefined) {
        return '—';
    }
    return `${amount.toLocaleString()} Mythril`;
}

/**
 * Every CURRENT PUBLIC template whose name the backend's `name__icontains` filter
 * would match for `templateName`.
 *
 * This mirrors LeagueCurrencySummaryView exactly: it filters
 * `LeagueTemplate.get_active_templates_for_week(year, week, 'PUBLIC')` by
 * `name__icontains=<name>` and takes `.first()`. `getPublicLeagueTemplates()`
 * returns that SAME set (auth_views PublicLeagueTemplatesView uses the identical
 * `get_active_templates_for_week(..., 'PUBLIC')` call), so counting matches here
 * tells us precisely whether that `.first()` is unambiguous.
 *
 * More than one match ⇒ the backend would silently pick a row we cannot identify —
 * possibly a DIFFERENT template than the one clicked, whose entry_cost/can_afford
 * would then be shown for, and never charged on, this arena. Deliberately NOT
 * trimmed: the backend does not trim either, and we must model its filter, not a
 * tidied-up version of it.
 */
function templatesMatchingName(templateName, allTemplates) {
    if (typeof templateName !== 'string') {
        throw new Error(
            `❌ INVALID TEMPLATE: expected a string template_name, got ${JSON.stringify(templateName)}`
        );
    }
    // Without the full candidate set there is no uniqueness check — and a missing set
    // must never read as "no competing names", which would wave the ambiguous case
    // straight through to a possibly wrong price.
    if (!Array.isArray(allTemplates)) {
        throw new Error(
            `❌ MISSING TEMPLATE SET: cannot verify that "${templateName}" resolves uniquely without the full public template list, got ${JSON.stringify(allTemplates)}`
        );
    }
    const needle = templateName.toLowerCase();
    return allTemplates.filter(
        (candidate) =>
            typeof candidate.template_name === 'string' &&
            candidate.template_name.toLowerCase().includes(needle)
    );
}

/**
 * Renders "current/max teams" for an arena row.
 *
 * The public-template contract only reports a fill count when a joinable league
 * ALREADY exists for that template (`available_league`). When it doesn't, the
 * backend creates the league at join time, so no fill count exists yet — that side
 * renders "—". We never print "0/12" for a league that does not exist.
 */
function formatFill(currentTeams, maxTeams) {
    const current = currentTeams === null || currentTeams === undefined ? '—' : currentTeams;
    const max = maxTeams === null || maxTeams === undefined ? '—' : maxTeams;
    return `${current}/${max} filled`;
}

/**
 * LeagueSignUp — Join-a-League flow
 *
 * Four internal screens driven by local state (no routing changes):
 *   'join'      — Screen 1: Quick Match / Custom Match + Public/Private radio
 *   'configure' — Screen 2: Configure League (FOUR match type cards, per spec)
 *   'arena'     — Screen 3: Select Public Arena
 *                   · arenaKind 'daily'  → LIVE. Real public league templates from
 *                     GET v2/leagues/draft-team/; JOIN starts a real draft — but ONLY
 *                     once that row's entry fee is known (see renderArenaRow /
 *                     handleJoinArena: the backend charges get_entry_cost() after the
 *                     team is created, so an unpriced join spends unapproved Mythril).
 *                   · arenaKind 'season' → LIVE. Forming season leagues from
 *                     GET v2/season-leagues/available/ (JOIN) plus the season
 *                     templates from GET v2/season-league-templates/ (CREATE).
 *                     Season entry costs no Mythril — the season create/join
 *                     endpoints charge nothing — so there is no fee gate here,
 *                     unlike the daily path.
 *   'private'   — Screen 4: Private League screen (Join / Create Private League CTAs)
 *                 + the Enter Invite Code modal ON TOP of it. Reached from Quick Match
 *                 with Private selected, which is the spec's Screen 4: the dimmed
 *                 backdrop behind the modal is THIS screen, not 'join'.
 *
 * WHICH CONTROL DECIDES WHAT (deliberate, and the reason Custom Match no longer
 * branches on the radio):
 *   · The Public/Private radio on 'join' governs QUICK MATCH ONLY — Public goes
 *     straight to the live daily arena, Private goes to the private screen with the
 *     invite-code modal already open. It is a shortcut selector, not a mode the rest
 *     of the flow inherits.
 *   · CUSTOM MATCH always goes to Configure, whatever the radio says, and the
 *     Configure card the user picks is the single source of truth for the path taken
 *     from there — including Create Private League. Nothing downstream of Configure
 *     reads `leagueType`, so "radio says Public, card says Create Private League" is
 *     not a contradiction: the radio simply has no say on this path. The Join screen
 *     labels the radio accordingly so the UI does not imply otherwise.
 *
 * BACK NAVIGATION is history-driven, not hardcoded per screen. `goToScreen()` pushes
 * the screen being left onto `screenHistory`; the header Back pops it. That matters
 * because 'arena' is reachable from TWO screens:
 *
 *   join → Quick Match (Public)                        → arena   ⇒ Back → join
 *   join → Quick Match (Private)                       → private ⇒ Back → join
 *   join → Custom Match → configure → Daily Draft       → arena   ⇒ Back → configure
 *   join → Custom Match → configure → Season Campaign   → arena   ⇒ Back → configure
 *   join → Custom Match → configure → Boss Fight        → leaves this flow entirely
 *   join → Custom Match → configure → Create Private    → modal over configure
 *   join → Custom Match → configure                              ⇒ Back → join
 *   join                                                         ⇒ Back → onMyTeams()
 *
 * The invite modal is NOT a screen: Quick Match (Private) makes ONE forward move
 * ('join' → 'private') and opens the modal over it, so cancelling the modal leaves the
 * user on the private screen and a single Back returns to 'join'.
 *
 * The last two Configure destinations never call goToScreen(), so they push NOTHING:
 * Boss Fight swaps the whole page via onBossBattles(), and Create Private League
 * opens AppRouter's team-name modal on top of Configure. The stack is left exactly as
 * Configure found it, so Back from Configure still returns to 'join'.
 *
 * The user's earlier answers (`leagueType`, `matchType`) are NEVER reset by Back, so
 * returning to Configure still shows the match type they picked.
 */
function LeagueSignUp({
    user,
    onDraftTeam,
    onCreatePrivate,
    onJoinPrivate,
    onJoinSeason,
    onCreateSeason,
    onMyTeams,
    onBack,
    onBossBattles,
    onAccount,
}) {
    const [screen, setScreen] = useState(ROOT_SCREEN);

    // ---- Back-navigation history ----
    //
    // A STACK, not a single "previous screen" pointer: the arena screen is reachable
    // from TWO different places (Join → Quick Match, and Configure → Continue), so a
    // single pointer would have to be re-derived on every entry and would silently go
    // wrong the moment a third entry path is added. The stack records where each
    // forward step came from, so Back always returns to the screen the user actually
    // left — Configure stays Configure, Join stays Join.
    //
    // Depth is bounded by the flow itself (max 'join' → 'configure' → 'arena'), so
    // there is nothing to trim. Modals (invite code, insufficient funds) do NOT change
    // `screen` and therefore never touch this stack — Quick Match (Private) pushes
    // history because it CHANGES SCREEN to 'private', not because it opens a modal.
    const [screenHistory, setScreenHistory] = useState([]);

    // Screen 1 state
    const [leagueType, setLeagueType] = useState('public');

    // Screen 2 state
    const [matchType, setMatchType] = useState(DEFAULT_MATCH_TYPE);

    // ---- Boss Fight rollout gate ----
    //
    // The Configure "Boss Fight" card is one of THREE places the Boss Battles
    // option is surfaced (the others being the My Teams destination and the two
    // routes), and all of them read this same hook so they cannot disagree.
    //
    // Hidden while the read is in flight, so the card never flashes in and then
    // disappears. See useBossBattlesFeatureFlag for the fail-closed rule.
    const { isVisible: bossBattlesVisible } = useBossBattlesFeatureFlag(user);

    const visibleMatchTypes = MATCH_TYPES.filter(
        (option) => option.id !== 'bossFight' || bossBattlesVisible
    );

    // A user can be sitting on the Boss Fight card when the flag read settles
    // (or when it is flipped off and the app is re-opened). Leaving `matchType`
    // pointing at a card that is no longer rendered would leave the radio group
    // with nothing checked while Continue still navigated into the hidden mode.
    // Fall back to the never-gated default instead.
    useEffect(() => {
        if (matchType === 'bossFight' && !bossBattlesVisible) {
            console.log(
                '🔒 LeagueSignUp: Boss Fight is not available — resetting the match ' +
                `type selection to "${DEFAULT_MATCH_TYPE}"`
            );
            setMatchType(DEFAULT_MATCH_TYPE);
        }
    }, [matchType, bossBattlesVisible]);

    // Screen 3 state
    // Which flavour of arena the screen lists — 'daily' or 'season'. Both are
    // LIVE, and they are backed by entirely DIFFERENT endpoints (weekly public
    // templates vs the season discovery API), which is why each keeps its own
    // list/loading/error state below rather than sharing one.
    const [arenaKind, setArenaKind] = useState('daily');
    const [arenaFilter, setArenaFilter] = useState('');
    const [arenaPage, setArenaPage] = useState(1);

    // Season arena state (arenaKind === 'season')
    const [seasonLeagues, setSeasonLeagues] = useState([]);
    const [seasonTemplates, setSeasonTemplates] = useState([]);
    const [seasonLoading, setSeasonLoading] = useState(false);
    const [seasonError, setSeasonError] = useState(null);
    // A 403 SEASON_FEATURE_DISABLED is an expected answer for a user the
    // feature flag doesn't cover — it must read as "not available to you",
    // never as a broken screen or as "no leagues exist".
    const [seasonFeatureDisabled, setSeasonFeatureDisabled] = useState(false);

    // Live public-template list (the daily-draft arena source)
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState(null);

    // Per-template entry cost, keyed by template_id.
    // Shape: { status: 'loading' | 'known' | 'unknown', entryCost, canAfford, shortfall, userBalance }
    // 'unknown' is a FIRST-CLASS state, never collapsed to 0 — see formatEntryFee().
    const [templateCosts, setTemplateCosts] = useState({});

    // Header currency state (shared by ALL four screens)
    const [currencyBalance, setCurrencyBalance] = useState(null);
    const [balanceError, setBalanceError] = useState(null);
    const [balanceLoading, setBalanceLoading] = useState(false);

    // Screen 4 state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [inviteError, setInviteError] = useState(null);

    // Insufficient-funds gate (restored from the develop-era draft flow)
    const [insufficientFunds, setInsufficientFunds] = useState(null);

    // ---- Request lifecycle guards ----

    // Guards against setState after the flow unmounts mid-request.
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // ---- Stale-response guards ----
    //
    // All three live reads can overlap: the balance is read on mount, from the header
    // Retry and on entering the arena screen; the template list is read on entering
    // the arena screen and from its Retry; the per-row fees are read after every
    // template load and from each row's Retry fee. A monotonically increasing request
    // id per read means only the newest may write state — value, error AND loading —
    // so an older response can never overwrite a newer one, and an older FAILURE can
    // never paint an error over a fresh success.
    const balanceRequestIdRef = useRef(0);
    const templatesRequestIdRef = useRef(0);
    // Season arena reads get their OWN generation counter: the two arena kinds
    // are separate endpoints reachable in either order, and a late season
    // response must never land in a daily visit (or vice versa).
    const seasonRequestIdRef = useRef(0);

    // Fee reads need one generation PER TEMPLATE, not a single shared counter: every
    // visible row fetches its own fee concurrently, so a shared id would mark all but
    // the last-issued row superseded and no fee would ever render.
    //
    // Map of { [templateId]: newestRequestId }. Read through templateCostOutcome().
    // Cleared by enterArena() so every fee read from a previous arena visit settles as
    // 'superseded' — see the spend-protection note on templateCostOutcome().
    const templateCostIdsRef = useRef({});
    const nextTemplateCostIdRef = useRef(0);

    /**
     * SINGLE source of truth for the header Mythril balance.
     *
     * Every balance read in this flow goes through here — the mount-time fetch,
     * the header retry, and the arena screen's refresh — so `currencyBalance` and
     * `balanceError` always describe the SAME attempt.
     *
     * Never rejects: it reports failure through `balanceError` so callers (and the
     * retry onClick) can't produce an unhandled rejection. The failure is still
     * surfaced loudly — console.error with full context + a visible UI error state.
     */
    const loadBalance = useCallback(async () => {
        const requestId = ++balanceRequestIdRef.current;
        setBalanceLoading(true);
        try {
            const balanceData = await getUserCurrencyBalance();
            const outcome = requestOutcome(requestId, balanceRequestIdRef, isMountedRef);
            if (outcome !== 'current') {
                // Expected, not a failure — debug level, never console.error.
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} balance response (request #${requestId}, newest #${balanceRequestIdRef.current})`
                );
                return;
            }
            if (!balanceData || balanceData.current_balance === undefined) {
                throw new Error('❌ INVALID API RESPONSE: Expected "current_balance" in currency balance response');
            }
            setCurrencyBalance(balanceData.current_balance);
            setBalanceError(null);
        } catch (error) {
            const outcome = requestOutcome(requestId, balanceRequestIdRef, isMountedRef);
            if (outcome !== 'current') {
                // A superseded FAILURE must not overwrite a newer success — otherwise
                // the header shows "balance unavailable" while a good value is loaded.
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} balance failure (request #${requestId}, newest #${balanceRequestIdRef.current}): ${error.message}`
                );
                return;
            }
            console.error('❌ LeagueSignUp: Failed to load Mythril balance for the header:', error);
            // Do NOT swallow: the header must show "unknown balance", never a
            // permanent "…" that is indistinguishable from a slow load.
            setBalanceError(error.message || 'Balance unavailable');
        } finally {
            // A superseded settle must NOT re-enable the Retry button / drop the
            // "Retrying…" label while the newest balance read is still running.
            if (requestOutcome(requestId, balanceRequestIdRef, isMountedRef) === 'current') {
                setBalanceLoading(false);
            }
        }
    }, []);

    /**
     * Per-row entry cost, from GET v2/currency/league-summary/, which returns
     * { league_name, entry_cost, user_balance, can_afford, shortfall, ... }.
     *
     * THE ROW MUST BE IDENTIFIED THE SAME WAY THE CHARGE WILL BE. The eventual join
     * selects the arena by ID (`template_id`, plus `available_league.league_id` when
     * one exists) and is charged `league.get_entry_cost()`. The summary endpoint,
     * however, accepts only `league_id` or a NAME resolved with
     * `name__icontains(...).first()` — so a name query is only as precise as the name
     * happens to be. Two current public templates sharing a name, or one name
     * containing another ("Standard" vs "Standard Plus"), silently resolves to some
     * other row, and this card would then advertise a fee and an affordability verdict
     * belonging to a league the user is not joining. That is a wrong-price bug, not a
     * cosmetic one.
     *
     * So this resolves by ID wherever the endpoint permits it, and refuses the name
     * path when it cannot be proven unique:
     *
     *   1. `available_league` present ⇒ query `league_id`. Exact, and it prices the
     *      very league the JOIN will place the user in.
     *   2. No league yet (the backend creates one from this template at join time) ⇒
     *      the endpoint offers no template-ID lookup, so fall back to the name — but
     *      ONLY after confirming, against the same current-public template set the
     *      backend searches, that exactly one template matches it and that match is
     *      THIS row. Anything else is ambiguous and becomes status 'unknown'.
     *
     * 'unknown' is a safe outcome, not a broken one: the row shows "—" and JOIN stays
     * disabled, so an unpriced or mispriced entry can never be purchased. Showing a
     * neighbouring template's number would be far worse than showing none.
     *
     * STALE RESPONSES ARE DISCARDED, per row. Each call claims a generation in
     * `templateCostIdsRef` before awaiting, and only the newest generation for that
     * template may write `templateCosts`. Without it, leaving the arena and re-entering
     * (or hammering Retry fee) lets an older response land on top of a newer one — and
     * because this state also drives the JOIN enable gate, an old SUCCESS could
     * re-enable JOIN on a stale price/affordability verdict, or an old FAILURE could
     * blank a fresh quote. Both are corrected here; see templateCostOutcome().
     *
     * @param template      the arena row being priced
     * @param siblings      every template currently listed — the ambiguity check needs
     *                      the whole set, and it is passed in explicitly so this stays
     *                      free of the `templates` state (which the initial load writes
     *                      in the same tick it prices the rows).
     */
    const loadTemplateCost = useCallback(async (template, siblings) => {
        const { template_id: templateId, template_name: templateName } = template;
        const availableLeague = template.available_league;

        // Claim this row's newest-request slot BEFORE any await, so a Retry fee click
        // (or a re-entry into the arena) immediately retires whatever is already in
        // flight for this template.
        const requestId = ++nextTemplateCostIdRef.current;
        templateCostIdsRef.current[templateId] = requestId;

        setTemplateCosts((prev) => ({
            ...prev,
            [templateId]: { status: 'loading' },
        }));

        try {
            let summary;

            if (availableLeague && availableLeague.league_id) {
                // ID PATH — unambiguous, and prices the exact league being joined.
                summary = await getLeagueCurrencySummary(availableLeague.league_id, null);
            } else {
                // NAME PATH — permitted only when provably unique. See above.
                const matches = templatesMatchingName(templateName, siblings);

                if (matches.length !== 1 || matches[0].template_id !== templateId) {
                    const matchLabels = matches
                        .map((m) => `"${m.template_name}" (id ${m.template_id})`)
                        .join(', ');
                    const ambiguityError = new Error(
                        `❌ AMBIGUOUS TEMPLATE NAME: "${templateName}" (id ${templateId}) cannot be priced by name — ` +
                            `the currency summary resolves names with name__icontains(...).first(), and ${matches.length} ` +
                            `current public template(s) match it [${matchLabels || 'none'}]. Refusing to display another ` +
                            `template's entry cost for this arena; this row needs a joinable league (league_id) or a ` +
                            `template-ID lookup on the summary endpoint.`
                    );
                    // Distinguishable from a transient network failure so the row can
                    // drop its "Retry fee" button — retrying resolves the same
                    // ambiguity forever, and offering it would promise a recovery this
                    // screen cannot deliver.
                    ambiguityError.isAmbiguousTemplate = true;
                    throw ambiguityError;
                }

                summary = await getLeagueCurrencySummary(null, templateName);
            }

            const outcome = templateCostOutcome(
                templateId,
                requestId,
                templateCostIdsRef,
                isMountedRef
            );
            if (outcome !== 'current') {
                // Expected, not a failure — a newer read for this row (or a newer arena
                // visit) owns the fee now. Writing here would resurrect a stale price
                // and could re-enable JOIN on a quote nothing live still vouches for.
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} fee response for template ${templateId} (request #${requestId}, newest #${templateCostIdsRef.current[templateId]})`
                );
                return;
            }

            if (!summary || summary.entry_cost === undefined) {
                throw new Error(
                    `❌ INVALID API RESPONSE: Expected "entry_cost" in league currency summary for template "${templateName}" (id ${templateId})`
                );
            }

            setTemplateCosts((prev) => ({
                ...prev,
                [templateId]: {
                    status: 'known',
                    entryCost: summary.entry_cost,
                    canAfford: summary.can_afford,
                    shortfall: summary.shortfall,
                    userBalance: summary.user_balance,
                },
            }));
        } catch (error) {
            const outcome = templateCostOutcome(
                templateId,
                requestId,
                templateCostIdsRef,
                isMountedRef
            );
            if (outcome !== 'current') {
                // A superseded FAILURE must not overwrite a newer success either — that
                // would blank a freshly-quoted fee back to "—" and needlessly disable a
                // JOIN the user could legitimately make.
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} fee failure for template ${templateId} (request #${requestId}, newest #${templateCostIdsRef.current[templateId]}): ${error.message}`
                );
                return;
            }
            console.error(
                `❌ LeagueSignUp: Failed to load entry cost for template "${templateName}" (id ${templateId}) — the row will show "—" and JOIN stays disabled rather than quoting a fee we cannot attribute to this arena:`,
                error
            );
            // UNKNOWN, NOT ZERO, and never another row's number. Collapsing this to 0
            // would advertise a free entry the backend never agreed to.
            setTemplateCosts((prev) => ({
                ...prev,
                [templateId]: {
                    status: 'unknown',
                    costError: error.message,
                    // true ⇒ not retryable from this screen; needs backend support for
                    // a template-ID lookup (or a joinable league on this template).
                    ambiguous: error.isAmbiguousTemplate === true,
                },
            }));
        }
    }, []);

    /**
     * Loads the live public-league templates that back the daily-draft arena list.
     *
     * Contract (GET v2/leagues/draft-team/, validated in getPublicLeagueTemplates):
     *   templates[]: {
     *     template_id, template_name, template_description, display_order,
     *     is_default, max_teams, current_week,
     *     available_league: null | { league_id, league_name, current_teams, max_teams, status }
     *   }
     *
     * Never rejects: failure lands in `templatesError`, which renders a visible error
     * with a Retry — the list is never silently empty.
     */
    const loadTemplates = useCallback(async () => {
        const requestId = ++templatesRequestIdRef.current;
        setTemplatesLoading(true);
        setTemplatesError(null);
        try {
            const response = await getPublicLeagueTemplates();
            const outcome = requestOutcome(requestId, templatesRequestIdRef, isMountedRef);
            if (outcome !== 'current') {
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} template response (request #${requestId}, newest #${templatesRequestIdRef.current})`
                );
                return;
            }

            // getPublicLeagueTemplates() already validates the envelope and every
            // required field, throwing loudly otherwise — so reaching here means the
            // shape is trustworthy.
            const loaded = [...response.templates].sort(
                (a, b) => a.display_order - b.display_order
            );
            setTemplates(loaded);
            setTemplatesError(null);
            console.log(
                `✅ LeagueSignUp: ${loaded.length} public template(s) loaded for the daily arena list`
            );

            // Kick off the per-row cost reads. Deliberately NOT awaited: the arenas
            // are listed and joinable immediately, with each fee filling in (or
            // falling back to "—") independently.
            //
            // `loaded` — not the `templates` state — is passed as the sibling set:
            // setTemplates() above has not been applied yet in this tick, and the
            // ambiguity check must see the WHOLE current-public list to match the
            // backend's name__icontains resolution.
            loaded.forEach((template) => {
                loadTemplateCost(template, loaded);
            });
        } catch (error) {
            const outcome = requestOutcome(requestId, templatesRequestIdRef, isMountedRef);
            if (outcome !== 'current') {
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} template failure (request #${requestId}, newest #${templatesRequestIdRef.current}): ${error.message}`
                );
                return;
            }
            console.error('❌ LeagueSignUp: Failed to load public league templates for the arena list:', error);
            // Do NOT swallow: an empty list and a failed fetch must never look the
            // same. This drives a visible error block with a Retry button.
            setTemplates([]);
            setTemplatesError(error.message || 'Failed to load public arenas');
        } finally {
            if (requestOutcome(requestId, templatesRequestIdRef, isMountedRef) === 'current') {
                setTemplatesLoading(false);
            }
        }
    }, [loadTemplateCost]);

    /**
     * Loads the season arena screen's two lists, in ONE guarded pass:
     *   · GET v2/season-leagues/available/   — forming leagues that can be JOINED
     *   · GET v2/season-league-templates/    — shapes a new league can be CREATED from
     *
     * Both are needed together: a season arena with no open leagues is still
     * actionable (create one), and an empty template list means creation is
     * genuinely impossible — two different empty states that must not be
     * conflated.
     *
     * Never rejects. A 403 SEASON_FEATURE_DISABLED sets `seasonFeatureDisabled`
     * (an expected "you don't have access" answer); anything else sets
     * `seasonError`, which renders a visible retry. An empty list and a failed
     * fetch never look the same.
     */
    const loadSeasonArenas = useCallback(async () => {
        const requestId = ++seasonRequestIdRef.current;
        setSeasonLoading(true);
        setSeasonError(null);
        setSeasonFeatureDisabled(false);
        try {
            const [leagues, templates] = await Promise.all([
                getSeasonAvailableLeagues(),
                getSeasonLeagueTemplates(),
            ]);
            const outcome = requestOutcome(requestId, seasonRequestIdRef, isMountedRef);
            if (outcome !== 'current') {
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} season arena response (request #${requestId}, newest #${seasonRequestIdRef.current})`
                );
                return;
            }
            setSeasonLeagues(leagues);
            setSeasonTemplates(templates);
            console.log(
                `✅ LeagueSignUp: ${leagues.length} open season league(s), ${templates.length} season template(s) loaded`
            );
        } catch (error) {
            const outcome = requestOutcome(requestId, seasonRequestIdRef, isMountedRef);
            if (outcome !== 'current') {
                console.debug(
                    `🕒 LeagueSignUp: discarding ${outcome} season arena failure (request #${requestId}): ${error.message}`
                );
                return;
            }
            if (isSeasonFeatureDisabled(error)) {
                // Not a failure — the flag simply doesn't cover this user.
                console.log('🔒 LeagueSignUp: season mode is not enabled for this account');
                setSeasonLeagues([]);
                setSeasonTemplates([]);
                setSeasonFeatureDisabled(true);
                return;
            }
            console.error('❌ LeagueSignUp: Failed to load season arenas:', error);
            setSeasonLeagues([]);
            setSeasonTemplates([]);
            setSeasonError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to load season campaigns'
            );
        } finally {
            if (requestOutcome(requestId, seasonRequestIdRef, isMountedRef) === 'current') {
                setSeasonLoading(false);
            }
        }
    }, []);

    // Entering the arena screen refreshes the header balance, then loads
    // whichever arena kind is being shown. Both kinds are live; they just read
    // different endpoints.
    useEffect(() => {
        if (screen !== 'arena') {
            return;
        }
        loadBalance();
        if (arenaKind === 'daily') {
            loadTemplates();
        } else {
            loadSeasonArenas();
        }
    }, [screen, arenaKind, loadBalance, loadTemplates, loadSeasonArenas]);

    // Fetch the currency balance once on mount so the header can show it on every
    // screen of the flow.
    useEffect(() => {
        loadBalance();
    }, [loadBalance]);

    // ---- Navigation ----

    /**
     * The ONLY way to move forward a screen. Pushes the screen being left onto the
     * history stack so Back can return to it.
     *
     * Every forward transition must go through here — a bare `setScreen` would advance
     * the flow without recording an origin, and Back would then land on whatever the
     * PREVIOUS forward step recorded (the exact class of bug this replaces).
     */
    const goToScreen = (nextScreen) => {
        if (!SCREEN_IDS.includes(nextScreen)) {
            throw new Error(
                `❌ UNKNOWN SCREEN in LeagueSignUp forward navigation: ${nextScreen} (expected one of ${SCREEN_IDS.join(', ')})`
            );
        }
        // Self-navigation would push a screen onto its own history and make Back a
        // no-op that looks broken. There is no legitimate caller, so fail loudly.
        if (nextScreen === screen) {
            throw new Error(
                `❌ INVALID NAVIGATION in LeagueSignUp: already on screen "${screen}" — forward navigation must change the screen`
            );
        }
        setScreenHistory((prev) => [...prev, screen]);
        setScreen(nextScreen);
    };

    const handleHeaderBack = () => {
        if (!SCREEN_IDS.includes(screen)) {
            throw new Error(`❌ UNKNOWN SCREEN in LeagueSignUp back handler: ${screen}`);
        }

        // The root screen has no in-flow origin — Back leaves the flow entirely.
        if (screen === ROOT_SCREEN) {
            // A non-empty stack while sitting on the root screen means a forward step
            // pushed history it never consumed — a real bookkeeping bug, not a
            // recoverable state. Refuse to paper over it.
            if (screenHistory.length > 0) {
                throw new Error(
                    `❌ CORRUPT NAVIGATION HISTORY in LeagueSignUp: on root screen "${ROOT_SCREEN}" with a non-empty history [${screenHistory.join(' → ')}]`
                );
            }
            // Leaving the flow entirely — back to My Teams / Leagues hub
            if (onMyTeams) {
                onMyTeams();
            }
            return;
        }

        // Any non-root screen was reached through goToScreen(), so it MUST have an
        // origin recorded. An empty stack here means a forward path bypassed
        // goToScreen() — never silently guess a destination.
        if (screenHistory.length === 0) {
            throw new Error(
                `❌ EMPTY NAVIGATION HISTORY in LeagueSignUp: cannot go back from "${screen}" — every non-root screen must be entered through goToScreen()`
            );
        }

        const origin = screenHistory[screenHistory.length - 1];
        if (!SCREEN_IDS.includes(origin)) {
            throw new Error(`❌ UNKNOWN SCREEN in LeagueSignUp navigation history: ${origin}`);
        }

        setScreenHistory((prev) => prev.slice(0, -1));
        setScreen(origin);
        // NOTHING ELSE IS RESET. `leagueType` and `matchType` deliberately survive the
        // trip back so the user returns to the choices they made — see the state-
        // preservation note on enterArena().
    };

    /**
     * Forward into the arena screen.
     *
     * `arenaFilter`/`arenaPage` ARE reset: they describe the list this entry is about
     * to load, and carrying a filter from a previous visit would hide arenas the user
     * never filtered out on this one. `leagueType`/`matchType` are NOT touched — they
     * are the user's answers to earlier screens, and Back must show them again.
     *
     * Also invalidates any template read AND any per-row fee read still in flight from
     * an earlier arena visit, so a response issued before the user went Back can never
     * land in this one.
     */
    const enterArena = (kind) => {
        if (kind !== 'daily' && kind !== 'season') {
            throw new Error(`❌ UNKNOWN ARENA KIND in LeagueSignUp: ${kind}`);
        }

        // Retire in-flight template reads from the PREVIOUS arena visit. Bumping the
        // id makes `requestOutcome()` classify them as 'superseded', so a response
        // issued before the user pressed Back can never write templates, an error, or
        // the loading flag into THIS visit.
        templatesRequestIdRef.current += 1;
        // Retire the previous visit's FEE reads the same way. Emptying the map means
        // `templateCostIdsRef.current[templateId]` is `undefined` for every in-flight
        // fee request, which can never equal its real numeric id — so all of them
        // settle as 'superseded'. Without this, a fee response from the previous visit
        // could land in this one and re-enable JOIN on a price no live request backs
        // (the row's own id is unchanged across visits, so the id alone can't tell the
        // two visits apart). `nextTemplateCostIdRef` is deliberately NOT reset: ids stay
        // globally monotonic, so a stale response can never collide with a fresh one.
        templateCostIdsRef.current = {};
        // The previous visit's list is no longer vouched for by any live request, so it
        // must not stay on screen while the new read runs.
        setTemplates([]);
        setTemplatesError(null);
        // Same reasoning for the fees: a price from the previous visit must not be
        // displayed — or joinable — while this visit's reads are still running.
        setTemplateCosts({});
        // Retire the previous visit's SEASON reads the same way, and clear the
        // lists they wrote: both arena kinds are reachable in either order, so a
        // season response issued before the user switched to daily must never
        // repaint a list this visit isn't showing.
        seasonRequestIdRef.current += 1;
        setSeasonLeagues([]);
        setSeasonTemplates([]);
        setSeasonError(null);
        setSeasonFeatureDisabled(false);

        // Pre-arm the loading state so the first paint of the arena screen says
        // "Loading…" rather than flashing a misleading empty state in the frame
        // before the entry effect fires. Each kind arms only its OWN flag, so a
        // spinner never appears for a read that will not happen.
        setTemplatesLoading(kind === 'daily');
        setSeasonLoading(kind === 'season');

        setArenaKind(kind);
        setArenaFilter('');
        setArenaPage(1);
        goToScreen('arena');
    };

    const handleQuickMatch = () => {
        if (leagueType === 'public') {
            // Quick Match (Public) goes straight to the LIVE daily arena list.
            // Back from there returns HERE ('join'), because that is what got pushed.
            enterArena('daily');
        } else {
            // Quick Match (Private) ADVANCES to the private screen and opens the invite
            // modal on top of it — per the spec's Screen 4, whose backdrop behind the
            // dimmed modal is the Private League screen with its Join/Create CTAs, NOT
            // the Join screen.
            //
            // This is also the ONLY forward path into 'private'. Without it
            // renderPrivateScreen() is unreachable dead code and cancelling the modal
            // drops the user back on Join with no way to reach "Create Private League"
            // from the private flow.
            //
            // The screen change goes through goToScreen(), so 'join' is pushed and Back
            // from the private screen returns to it. The modal itself still touches no
            // history — it only flips `showInviteModal`.
            goToScreen('private');
            setShowInviteModal(true);
        }
    };

    /**
     * Custom Match ALWAYS goes to Configure League — per the spec, Configure is the
     * structured path and its four cards (including Create Private League) are the
     * ONLY place a match type is chosen.
     *
     * The Public/Private radio is deliberately NOT consulted here. It previously
     * short-circuited Private straight into the create-private modal, which skipped
     * Configure entirely and made the private option on Configure unreachable — two
     * competing sources of truth for the same decision. The radio now governs Quick
     * Match only (see handleQuickMatch); on the Custom path the Configure selection
     * wins, and the Join screen says so under the League Type label.
     */
    const handleCustomMatch = () => {
        goToScreen('configure');
    };

    /**
     * Continue on Configure League — one destination per MATCH_TYPES entry.
     *
     * Two of the four leave this flow entirely (Boss Fight navigates away, Create
     * Private League opens the team-name modal owned by AppRouter). Neither calls
     * goToScreen(), so neither pushes anything onto `screenHistory` — the stack stays
     * exactly as Configure left it, which is what keeps Back honest if the user
     * returns to this flow later.
     *
     * The default branch THROWS: a new card added to MATCH_TYPES without a
     * destination must fail loudly here, not silently no-op on click.
     */
    const handleConfigureContinue = () => {
        switch (matchType) {
            case 'dailyDraft':
                enterArena('daily');
                break;
            case 'seasonCampaign':
                enterArena('season');
                break;
            case 'bossFight':
                // BELT AND BRACES. The card is filtered out of the radio group
                // when the flag is off, so this should be unreachable — but the
                // selection and the visibility are separate pieces of state and
                // Continue must never navigate into a withdrawn mode if they
                // ever fall out of step. Not an error: the flag legitimately
                // flips off mid-session.
                if (!bossBattlesVisible) {
                    console.log(
                        '🔒 LeagueSignUp: ignoring Continue for Boss Fight — the mode is ' +
                        'not available to this user'
                    );
                    return;
                }
                if (!onBossBattles) {
                    throw new Error('❌ MISSING PROP: LeagueSignUp requires onBossBattles to navigate to the Boss Battles page');
                }
                onBossBattles();
                break;
            case 'createPrivate':
                // Hands off to the SAME create-private flow the private screen's CTA
                // uses — no template id, no fabricated entry cost. See
                // handleCreatePrivateLeague() for why both omissions are load-bearing.
                handleCreatePrivateLeague();
                break;
            default:
                throw new Error(`❌ UNKNOWN MATCH TYPE in LeagueSignUp continue handler: ${matchType}`);
        }
    };

    const handleOpenInviteModal = () => {
        setShowInviteModal(true);
    };

    /**
     * The ONE create-private-league entry point, shared by BOTH callers:
     * Configure League → "Create Private League" → Continue, and the private screen's
     * "Create Private League" CTA. Single function so neither can drift into passing
     * arguments the other doesn't.
     */
    const handleCreatePrivateLeague = () => {
        if (!onCreatePrivate) {
            throw new Error('❌ MISSING PROP: LeagueSignUp requires onCreatePrivate to open the create-private-league modal');
        }
        // NO TEMPLATE ID BY DESIGN. The redesign removed the template-selector
        // dropdown, so this screen has no user-selected template to pass — and
        // inventing one is a real production bug: the backend validates a supplied
        // id against (is_active, league_type='PRIVATE', current year+week), so any
        // guessed id fails or targets the wrong league on most databases.
        // CreatePrivateLeagueView resolves the correct current-week PRIVATE default
        // itself when template_id is absent (backend/game/api/views/auth_views.py:781-797),
        // so absence is the correct, authoritative signal. Do NOT reintroduce a
        // literal here without a real selector feeding it.
        //
        // NO ENTRY COST EITHER — the second argument is likewise omitted. This screen
        // has no fee data for a not-yet-created private league, and passing 0 would
        // make TeamNameModal advertise a FREE entry the backend never agreed to.
        // Omitted → leagueInfo.entryFee is undefined → the modal renders "—".
        onCreatePrivate();
    };

    const handleInviteSubmit = () => {
        if (!onJoinPrivate) {
            throw new Error('❌ MISSING PROP: LeagueSignUp requires onJoinPrivate to submit an invite code');
        }

        const code = inviteCode.trim();
        if (!code) {
            setInviteError('Enter your invite code to join.');
            return;
        }

        console.log(`🔑 LeagueSignUp: submitting invite code "${code}"`);
        setInviteError(null);
        setShowInviteModal(false);
        setInviteCode('');

        // Hands the code to AppRouter.handleJoinPrivate, which routes it into the
        // same team-naming + join flow the legacy private-code modal used.
        onJoinPrivate(code);
    };

    const handleInviteCancel = () => {
        setShowInviteModal(false);
        setInviteCode('');
        setInviteError(null);
    };

    /**
     * JOIN on a live daily arena row — the restored public-draft entry point.
     *
     * Reproduces the develop-era `handleDraftTeam`: cost gate, affordability gate,
     * then `onDraftTeam(templateId, entryCost, selectedArena)`.
     *
     * Three deliberate differences from develop, all about honesty:
     *  1. THE FEE MUST BE KNOWN BEFORE A JOIN CAN START. See the cost gate below —
     *     this is a spend-protection rule, not a UX nicety.
     *  2. `entryCost` is therefore always a real backend number. develop used
     *     `templateCost?.cost || 0`; a 0 there travels into TeamNameModal's
     *     `entryFee` and advertises a FREE entry the backend never agreed to.
     *  3. When the template already has an `available_league`, that league is passed
     *     as the third argument so the user joins THE arena they clicked. Without it
     *     AppRouter falls back to "first draftable league", which would silently
     *     place the user in a different arena than the row they tapped.
     */
    const handleJoinArena = (template) => {
        if (!onDraftTeam) {
            throw new Error('❌ MISSING PROP: LeagueSignUp requires onDraftTeam to start a public draft');
        }

        const templateId = template.template_id;
        if (!templateId) {
            throw new Error(
                `❌ MISSING TEMPLATE_ID: arena row has no template_id: ${JSON.stringify(template)}`
            );
        }

        const cost = templateCosts[templateId];

        // COST GATE — NO JOIN WITHOUT A KNOWN FEE.
        //
        // The backend is authoritative about the CHARGE, not about CONSENT:
        // DraftTeamView.post creates the team and then charges
        // `league.get_entry_cost()` unconditionally. If we let a join start while the
        // per-template currency summary is still loading — or after it failed — the
        // confirmation modal shows an unknown wager ("—") and the user spends Mythril
        // without ever having been told the amount. A transient failure of the fee
        // endpoint must cost the user nothing.
        //
        // The row's JOIN button is disabled in exactly this state and offers a fee
        // retry instead (see renderArenaRow), so reaching here means a caller bypassed
        // the UI gate. Fail loudly rather than start a charge we can't price.
        if (!cost || cost.status !== 'known') {
            throw new Error(
                `❌ ENTRY COST UNKNOWN: refusing to join template ${templateId} — fee status "${
                    cost ? cost.status : 'not requested'
                }". JOIN must stay disabled until the currency summary resolves; the backend charges get_entry_cost() regardless of what this screen showed.`
            );
        }

        // AFFORDABILITY GATE — safe to judge now that the cost is known, and every
        // number handed to the modal is real backend data.
        if (cost.canAfford === false) {
            console.log(
                `💎 LeagueSignUp: blocking join of template ${templateId} — entry ${cost.entryCost}, balance ${cost.userBalance}, shortfall ${cost.shortfall}`
            );
            setInsufficientFunds({
                entryCost: cost.entryCost,
                userBalance: cost.userBalance,
                shortfall: cost.shortfall,
            });
            return;
        }

        // Always a real number by this point — the cost gate above guarantees it.
        const entryCost = cost.entryCost;

        const selectedArena = template.available_league
            ? {
                  league_id: template.available_league.league_id,
                  league_name: template.available_league.league_name,
                  current_teams: template.available_league.current_teams,
                  max_teams: template.available_league.max_teams,
              }
            : null;

        console.log(
            `🎯 LeagueSignUp: starting public draft — template_id ${templateId}, entryCost ${entryCost}, arena ${
                selectedArena ? selectedArena.league_id : '(none — backend will resolve/create)'
            }`
        );

        onDraftTeam(templateId, entryCost, selectedArena);
    };

    /**
     * JOIN on a season arena row.
     *
     * Hands off to AppRouter, which collects a team name in the SAME
     * TeamNameModal the daily/private flows use and then POSTs
     * v2/season-leagues/{id}/join/. `team_name` is required by that endpoint,
     * so a row cannot be joined without the modal step.
     *
     * NO FEE GATE, and that is a contract fact rather than an omission: the
     * season create/join endpoints charge no Mythril at all (neither calls
     * get_entry_cost()), so there is no price the user must be told before
     * consenting. The daily path's cost gate exists precisely because
     * DraftTeamView *does* charge; copying it here would block a join on a fee
     * that will never be taken.
     */
    const handleJoinSeasonLeague = (league) => {
        if (!onJoinSeason) {
            throw new Error('❌ MISSING PROP: LeagueSignUp requires onJoinSeason to join a season league');
        }
        if (!league.league_id) {
            throw new Error(
                `❌ MISSING LEAGUE_ID: season arena row has no league_id: ${JSON.stringify(league)}`
            );
        }
        console.log(`🏆 LeagueSignUp: joining season league ${league.league_id} ("${league.name}")`);
        onJoinSeason(league);
    };

    /**
     * CREATE from a season template row — same modal handoff, different
     * endpoint (POST v2/season-leagues/).
     *
     * The template id is REQUIRED by the backend: unlike weekly private
     * creation there is no current-week default to fall back on, so it is
     * always taken from a real row and never inferred.
     */
    const handleCreateSeasonLeague = (template) => {
        if (!onCreateSeason) {
            throw new Error('❌ MISSING PROP: LeagueSignUp requires onCreateSeason to create a season league');
        }
        if (!template.template_id) {
            throw new Error(
                `❌ MISSING TEMPLATE_ID: season template row has no template_id: ${JSON.stringify(template)}`
            );
        }
        console.log(
            `🏆 LeagueSignUp: creating season league from template ${template.template_id} ("${template.name}")`
        );
        onCreateSeason(template);
    };

    // ---- Shared header ----
    // App-standard header pattern — see AIDocs/Guides/ui/header-component-implementation-guide.md
    // Mirrors MyTeamsPage: back button left; currency display + user avatar right, on ALL screens.

    const handleAvatarClick = () => {
        if (!onAccount) {
            throw new Error('❌ MISSING PROP: LeagueSignUp requires onAccount to navigate to the account page');
        }
        onAccount();
    };

    // Balance display precedence: ERROR beats a stale value. If the most recent
    // balance read failed we show the unknown-balance treatment even when an older
    // successful value is still in state — the user must never be shown a number we
    // can no longer vouch for while deciding whether they can afford an entry fee.
    const renderCurrencyLabel = () => {
        if (balanceError) {
            return (
                <span className="currency-label currency-label--error">
                    <span className="currency-warning-icon" aria-hidden="true">⚠</span>
                    <span aria-hidden="true">— MYTHRIL</span>
                    <span className="ljf-sr-only">
                        Mythril balance unavailable — {balanceError}. Use the Retry balance button below the header.
                    </span>
                </span>
            );
        }
        if (currencyBalance !== null) {
            return (
                <span className="currency-label">
                    {`${currencyBalance.toLocaleString()} MYTHRIL`}
                </span>
            );
        }
        return (
            <span className="currency-label" aria-label="Loading Mythril balance">
                …
            </span>
        );
    };

    // The header's left control has ONE handler but TWO behaviours, so it must not
    // have one fixed label. On the root screen it leaves the flow entirely for the
    // Leagues hub ("LEAGUES" is the truth). On every other screen handleHeaderBack
    // pops ONE step of in-flow history and stays inside the flow — labelling that
    // "LEAGUES" promised a destination the click does not go to. The label is
    // derived from the same `screen`/ROOT_SCREEN condition handleHeaderBack
    // branches on, so the two can never disagree.
    const headerBackLabel = screen === ROOT_SCREEN ? 'LEAGUES' : 'BACK';

    const renderHeader = () => (
        <div className="page-header">
            <button className="header-back-button" onClick={handleHeaderBack}>
                <span className="back-arrow" aria-hidden="true">◀</span>
                <span className="back-label">{headerBackLabel}</span>
            </button>
            <div className="header-right-group">
                <div
                    className={`header-currency-button ${balanceError ? 'header-currency-button--error' : ''}`}
                    role="status"
                    aria-live="polite"
                >
                    <img src="/art/diamond_400_white_bg.svg" alt="Diamond" className="diamond-icon" />
                    {renderCurrencyLabel()}
                </div>
                <img
                    src="/art/UserIcon.png"
                    alt="User"
                    className="header-user-avatar"
                    onClick={handleAvatarClick}
                />
            </div>
        </div>
    );

    // Retry lives in a slim banner directly BENEATH the header, not inside it.
    // Rationale: `.page-header` is a fixed 78px bar whose right group is already a
    // 150px back button + a fixed 181px currency pill + the avatar — at a 375px
    // viewport there is no horizontal room for another control without shrinking
    // the currency pill or wrapping the bar. The banner keeps the header's fixed
    // height/geometry untouched, gives the retry a real tap target, and renders on
    // every screen because it sits at page level next to renderHeader().
    // Non-blocking: it never covers or gates the screen content below it.
    const renderBalanceErrorBanner = () => {
        if (!balanceError) {
            return null;
        }
        return (
            <div className="ljf-balance-banner" role="alert">
                <span className="ljf-balance-banner-message">
                    Couldn’t load your Mythril balance — entry fees may not be affordable.
                </span>
                <button
                    className="ljf-balance-retry"
                    onClick={loadBalance}
                    disabled={balanceLoading}
                >
                    {balanceLoading ? 'Retrying…' : 'Retry balance'}
                </button>
            </div>
        );
    };

    // ---- Screen 1: Join League ----

    const renderJoinScreen = () => (
        <div className="ljf-screen">
            <h1 className="ljf-heading">Join League</h1>

            <div className="ljf-cta-stack">
                <button className="ljf-cta ljf-cta--primary" onClick={handleQuickMatch}>
                    Quick Match
                </button>
                <button className="ljf-cta ljf-cta--gold" onClick={handleCustomMatch}>
                    Custom Match
                </button>
            </div>

            {/* Scope of this radio, stated in the UI so the two controls cannot read
                as contradictory: it steers Quick Match only. Custom Match always opens
                Configure League, whose card selection decides that path — including
                Create Private League, which is why "Public" here is not a conflict. */}
            <div className="ljf-section-label">League Type</div>
            <div className="ljf-section-hint">
                Applies to Quick Match — Custom Match uses your Configure League choice.
            </div>
            <div className="ljf-option-list" role="radiogroup" aria-label="League Type">
                <label
                    className={`ljf-option-row ${leagueType === 'public' ? 'ljf-option-row--selected' : ''}`}
                >
                    <input
                        type="radio"
                        name="league-type"
                        value="public"
                        checked={leagueType === 'public'}
                        onChange={() => setLeagueType('public')}
                        className="ljf-option-input"
                    />
                    <span className="ljf-option-radio" aria-hidden="true" />
                    <span className="ljf-option-title">Public</span>
                </label>
                <label
                    className={`ljf-option-row ${leagueType === 'private' ? 'ljf-option-row--selected' : ''}`}
                >
                    <input
                        type="radio"
                        name="league-type"
                        value="private"
                        checked={leagueType === 'private'}
                        onChange={() => setLeagueType('private')}
                        className="ljf-option-input"
                    />
                    <span className="ljf-option-radio" aria-hidden="true" />
                    <span className="ljf-option-title">Private</span>
                </label>
            </div>
        </div>
    );

    // ---- Screen 2: Configure League ----

    const renderConfigureScreen = () => (
        <div className="ljf-screen">
            <h1 className="ljf-heading">Configure League</h1>
            <p className="ljf-subheading">Structured path · Customize your league style</p>

            {/* Rollout-gated cards are filtered out entirely — see visibleMatchTypes. */}
            <div className="ljf-option-list" role="radiogroup" aria-label="Match Type">
                {visibleMatchTypes.map((option) => (
                    <label
                        key={option.id}
                        className={`ljf-option-row ljf-option-row--card ${
                            matchType === option.id ? 'ljf-option-row--selected' : ''
                        }`}
                    >
                        <input
                            type="radio"
                            name="match-type"
                            value={option.id}
                            checked={matchType === option.id}
                            onChange={() => setMatchType(option.id)}
                            className="ljf-option-input"
                        />
                        <span className="ljf-option-radio" aria-hidden="true" />
                        <span className="ljf-option-text">
                            <span className="ljf-option-title">{option.title}</span>
                            <span className="ljf-option-description">{option.description}</span>
                        </span>
                    </label>
                ))}
            </div>

            <div className="ljf-footer-bar">
                <span className="ljf-step-label">Step 1 of 2</span>
                <button className="ljf-continue-button" onClick={handleConfigureContinue}>
                    Continue →
                </button>
            </div>
        </div>
    );

    // ---- Screen 3: Select Public Arena ----
    //
    // BOTH arena kinds are live and joinable. 'daily' lists weekly public
    // templates (fee-gated); 'season' lists forming season leagues plus the
    // templates a new one can be created from (no fee — see
    // handleJoinSeasonLeague).

    // Filter over REAL template fields only — the template name/description and, when
    // one exists, the actual league name.
    const filteredTemplates = templates.filter((template) => {
        const needle = arenaFilter.trim().toLowerCase();
        if (!needle) {
            return true;
        }
        const haystack = [
            template.template_name,
            template.template_description,
            template.available_league ? template.available_league.league_name : '',
        ]
            .join(' ')
            .toLowerCase();
        return haystack.includes(needle);
    });

    const totalArenaPages = Math.ceil(filteredTemplates.length / ARENAS_PER_PAGE);
    // Clamp in case the filter shrank the list below the current page
    const safeArenaPage = Math.min(arenaPage, Math.max(totalArenaPages, 1));
    const pagedTemplates = filteredTemplates.slice(
        (safeArenaPage - 1) * ARENAS_PER_PAGE,
        safeArenaPage * ARENAS_PER_PAGE
    );

    const handleArenaFilterChange = (e) => {
        setArenaFilter(e.target.value);
        setArenaPage(1);
    };

    const renderArenaFee = (templateId) => {
        const cost = templateCosts[templateId];
        if (cost && cost.status === 'loading') {
            return 'Fee: loading…';
        }
        if (cost && cost.status === 'known') {
            return `Fee: ${formatEntryFee(cost.entryCost)}`;
        }
        // No entry yet, or the read failed — unknown, shown as "—".
        return `Fee: ${formatEntryFee(undefined)}`;
    };

    /**
     * A single live arena row.
     *
     * MOCKUP FIELDS THE CONTRACT CANNOT SUPPLY — and how each is adapted:
     *  · Per-arena league NAME: only exists when `available_league` is present. The
     *    row is therefore titled with the real `template_name` (always present) and
     *    the concrete league name is shown as a secondary line only when real.
     *  · "8/10 filled": only exists via `available_league.current_teams/max_teams`.
     *    With no league yet we print "—/{template.max_teams}" — the cap is real, the
     *    current count genuinely does not exist. We never print a fabricated 0.
     *  · Per-arena FEE: sourced per template from the currency summary. Unknown
     *    renders "—", a real 0 renders "Free".
     *
     * JOIN IS GATED ON A KNOWN FEE. While the fee is loading — and permanently, if
     * the read failed — the button is disabled, because DraftTeamView charges
     * `league.get_entry_cost()` after creating the team no matter what this row said.
     * Letting the click through would spend Mythril on an amount the user was shown
     * as "—" and never approved. A failed fee read gets its own Retry fee control so
     * the row is recoverable without reloading the whole arena list.
     *
     * TWO KINDS OF UNKNOWN FEE, shown differently because only one is recoverable:
     *  · A failed request (network/5xx) → retryable, keeps the "Retry fee" button.
     *  · An AMBIGUOUS template name (loadTemplateCost's name path) → NOT retryable.
     *    The summary endpoint can only be queried by league_id or name, the name
     *    matches more than one current public template, and retrying would resolve to
     *    the same wrong row every time. The row states that plainly instead of
     *    offering a button that cannot help.
     */
    const renderArenaRow = (template) => {
        const league = template.available_league;
        const fillCurrent = league ? league.current_teams : undefined;
        const fillMax = league ? league.max_teams : template.max_teams;

        const cost = templateCosts[template.template_id];
        const costStatus = cost ? cost.status : 'pending';
        const feeKnown = costStatus === 'known';
        // 'pending' (request not issued yet) and 'loading' are both transient; only
        // 'unknown' means the read actually failed or could not be attributed to this
        // template.
        const feeFailed = costStatus === 'unknown';
        const feeAmbiguous = feeFailed && cost.ambiguous === true;

        return (
            <div key={template.template_id} className="ljf-arena-card">
                <div className="ljf-arena-info">
                    <div className="ljf-arena-name">
                        <span className="ljf-arena-icon" aria-hidden="true">🏆</span>
                        {template.template_name}
                    </div>
                    {league && (
                        <div className="ljf-arena-subname">{league.league_name}</div>
                    )}
                    <div className="ljf-arena-meta">
                        {formatFill(fillCurrent, fillMax)} · {renderArenaFee(template.template_id)}
                    </div>
                    {!league && (
                        <div className="ljf-arena-note">
                            No open arena yet — joining opens a new one.
                        </div>
                    )}
                    {feeFailed && (
                        <div className="ljf-arena-fee-error" role="alert">
                            <span className="ljf-arena-fee-error-message">
                                {feeAmbiguous
                                    ? 'This arena shares its name with another open arena, so we can’t confirm which entry fee is yours. Joining is disabled rather than risk charging a different arena’s price.'
                                    : 'Couldn’t load the entry fee — joining is disabled until we know what this costs.'}
                            </span>
                            {/* Retry only where retrying can actually change the outcome. */}
                            {!feeAmbiguous && (
                                <button
                                    className="ljf-arena-fee-retry"
                                    onClick={() => loadTemplateCost(template, templates)}
                                >
                                    Retry fee
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <button
                    className="ljf-arena-join"
                    onClick={() => handleJoinArena(template)}
                    disabled={!feeKnown}
                    title={
                        feeKnown
                            ? undefined
                            : feeAmbiguous
                              ? 'Entry fee can’t be matched to this arena — its name is shared with another open arena'
                              : feeFailed
                                ? 'Entry fee unavailable — retry the fee before joining'
                                : 'Loading the entry fee…'
                    }
                >
                    JOIN
                </button>
            </div>
        );
    };

    const renderDailyArenaList = () => {
        if (templatesLoading && templates.length === 0) {
            return (
                <div className="ljf-arena-status" role="status">
                    Loading public arenas…
                </div>
            );
        }

        // Loud, visible failure with a retry — a failed fetch must never look like
        // "there are simply no arenas".
        if (templatesError) {
            return (
                <div className="ljf-arena-status ljf-arena-status--error" role="alert">
                    <span className="ljf-arena-error-message">
                        Couldn’t load public arenas — {templatesError}
                    </span>
                    <button
                        className="ljf-arena-retry"
                        onClick={loadTemplates}
                        disabled={templatesLoading}
                    >
                        {templatesLoading ? 'Retrying…' : 'Retry'}
                    </button>
                </div>
            );
        }

        return (
            <>
                <Input
                    type="text"
                    placeholder="Filter arenas..."
                    value={arenaFilter}
                    onChange={handleArenaFilterChange}
                    className="ljf-arena-filter"
                    aria-label="Filter arenas"
                />

                <div className="ljf-arena-list">
                    {pagedTemplates.map(renderArenaRow)}
                    {filteredTemplates.length === 0 && (
                        <div className="ljf-arena-empty">
                            {templates.length === 0
                                ? 'No public arenas are open this week'
                                : 'No arenas match your filter'}
                        </div>
                    )}
                </div>

                <Pagination
                    currentPage={safeArenaPage}
                    totalPages={totalArenaPages}
                    onPageChange={setArenaPage}
                    showFirstLast={false}
                    size="sm"
                    className="ljf-arena-pagination"
                />
            </>
        );
    };

    /**
     * One open season league — a real league that already exists and is
     * FORMING, with real seats. `teams_joined`/`team_count` both come from the
     * backend, so unlike the daily path this fill count is never unknown.
     */
    const renderSeasonLeagueRow = (league) => (
        <div key={league.league_id} className="ljf-arena-card">
            <div className="ljf-arena-info">
                <div className="ljf-arena-name">
                    <span className="ljf-arena-icon" aria-hidden="true">🏆</span>
                    {league.name}
                </div>
                <div className="ljf-arena-meta">
                    {formatFill(league.teams_joined, league.team_count)} · Starts week{' '}
                    {league.regular_season_start_week} · {league.season_year}
                </div>
            </div>
            <button className="ljf-arena-join" onClick={() => handleJoinSeasonLeague(league)}>
                JOIN
            </button>
        </div>
    );

    /**
     * One season league TEMPLATE — the shape of a league that does not exist
     * yet. CREATE makes it, and the creator becomes its commissioner.
     */
    const renderSeasonTemplateRow = (template) => (
        <div key={template.template_id} className="ljf-arena-card">
            <div className="ljf-arena-info">
                <div className="ljf-arena-name">
                    <span className="ljf-arena-icon" aria-hidden="true">🛠️</span>
                    {template.name}
                </div>
                <div className="ljf-arena-meta">
                    {template.team_count} teams · Weeks {template.regular_season_start_week}–
                    {template.championship_week} · {template.season_year}
                </div>
            </div>
            <button
                className="ljf-arena-join"
                onClick={() => handleCreateSeasonLeague(template)}
            >
                CREATE
            </button>
        </div>
    );

    /**
     * Season arena panel — LIVE.
     *
     * Two lists, deliberately kept separate because they mean different things:
     * open leagues you can JOIN right now, and templates you can CREATE a new
     * league from. "No open leagues" is a normal state that must not read as a
     * dead end while creation is available.
     *
     * Three distinct non-list states, none of which look alike:
     *   · loading            — a read is genuinely in flight
     *   · feature disabled   — an expected 403; season mode isn't enabled for you
     *   · error              — a real failure, with a Retry
     */
    const renderSeasonArenaList = () => {
        if (seasonLoading && seasonLeagues.length === 0 && seasonTemplates.length === 0) {
            return (
                <div className="ljf-arena-status" role="status">
                    Loading season campaigns…
                </div>
            );
        }

        if (seasonFeatureDisabled) {
            return (
                <div className="ljf-arena-status" role="status">
                    Season-long campaigns aren’t enabled for your account yet. Daily Draft
                    Battle and Private League are available now.
                </div>
            );
        }

        if (seasonError) {
            return (
                <div className="ljf-arena-status ljf-arena-status--error" role="alert">
                    <span className="ljf-arena-error-message">
                        Couldn’t load season campaigns — {seasonError}
                    </span>
                    <button
                        className="ljf-arena-retry"
                        onClick={loadSeasonArenas}
                        disabled={seasonLoading}
                    >
                        {seasonLoading ? 'Retrying…' : 'Retry'}
                    </button>
                </div>
            );
        }

        return (
            <>
                <div className="ljf-section-label">Open Campaigns</div>
                <div className="ljf-arena-list">
                    {seasonLeagues.length > 0 ? (
                        seasonLeagues.map(renderSeasonLeagueRow)
                    ) : (
                        <div className="ljf-arena-empty">
                            No season campaigns are forming right now — start one below.
                        </div>
                    )}
                </div>

                <div className="ljf-section-label">Start a New Campaign</div>
                <div className="ljf-arena-list">
                    {seasonTemplates.length > 0 ? (
                        seasonTemplates.map(renderSeasonTemplateRow)
                    ) : (
                        <div className="ljf-arena-empty">
                            No season league templates are active — new campaigns can’t be
                            created until one is configured.
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderArenaScreen = () => (
        <div className="ljf-screen">
            <h1 className="ljf-heading">Select Public Arena</h1>
            <p className="ljf-subheading">
                {arenaKind === 'season'
                    ? 'Season Campaigns — join an open league or start your own'
                    : 'Daily Drafts — pick an arena and draft your team'}
            </p>

            {arenaKind === 'season' ? renderSeasonArenaList() : renderDailyArenaList()}
        </div>
    );

    // ---- Screen 4: Private League + Invite Code modal ----

    const renderPrivateScreen = () => (
        <div className="ljf-screen">
            <h1 className="ljf-heading">Private League</h1>

            <div className="ljf-cta-stack">
                <button className="ljf-cta ljf-cta--gold-outline" onClick={handleOpenInviteModal}>
                    Join Private League
                </button>
                <button className="ljf-cta ljf-cta--gold-outline" onClick={handleCreatePrivateLeague}>
                    Create Private League
                </button>
            </div>
        </div>
    );

    // Invite Code modal — rendered at page level so it overlays whichever screen is active
    const renderInviteModal = () => (
        <Modal
            isOpen={showInviteModal}
            onClose={handleInviteCancel}
            size="sm"
            showCloseButton={false}
            className="ljf-invite-modal"
        >
            <div className="ljf-invite-content">
                <h2 className="ljf-invite-heading">Enter Invite Code</h2>
                <p className="ljf-invite-subtext">
                    Enter your 12-character private key to join your friends.
                </p>
                <input
                    type="text"
                    className="ljf-invite-input"
                    placeholder="OLYMPUS-772-GOLD"
                    value={inviteCode}
                    onChange={(e) => {
                        setInviteCode(e.target.value);
                        if (inviteError) {
                            setInviteError(null);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleInviteSubmit();
                        }
                    }}
                    aria-label="Invite code"
                    aria-invalid={inviteError ? 'true' : undefined}
                />
                {inviteError && (
                    <p className="ljf-invite-error" role="alert">{inviteError}</p>
                )}
                <button className="ljf-invite-join" onClick={handleInviteSubmit}>
                    Join League
                </button>
                <button className="ljf-invite-cancel" onClick={handleInviteCancel}>
                    Cancel
                </button>
            </div>
        </Modal>
    );

    /**
     * Insufficient-funds gate — restored from the develop-era draft flow.
     *
     * Only ever opened from a KNOWN cost (`status === 'known'` and
     * `canAfford === false`), so every number in here is real backend data. It is
     * never shown for an unknown cost, because we cannot judge affordability then.
     */
    const renderInsufficientFundsModal = () => (
        <Modal
            isOpen={insufficientFunds !== null}
            onClose={() => setInsufficientFunds(null)}
            size="sm"
            title="Not Enough Mythril"
        >
            <div className="ljf-funds-content">
                <div className="ljf-funds-row">
                    <span className="ljf-funds-label">Entry Cost</span>
                    <span className="ljf-funds-value">
                        {formatMythril(insufficientFunds?.entryCost)}
                    </span>
                </div>
                <div className="ljf-funds-row">
                    <span className="ljf-funds-label">Your Balance</span>
                    <span className="ljf-funds-value">
                        {formatMythril(insufficientFunds?.userBalance)}
                    </span>
                </div>
                <div className="ljf-funds-row ljf-funds-row--shortfall">
                    <span className="ljf-funds-label">You Need</span>
                    <span className="ljf-funds-value">
                        {formatMythril(insufficientFunds?.shortfall)} more
                    </span>
                </div>
                <button
                    className="ljf-funds-dismiss"
                    onClick={() => setInsufficientFunds(null)}
                >
                    Got It
                </button>
            </div>
        </Modal>
    );

    // ---- Render ----

    const renderScreen = () => {
        switch (screen) {
            case 'join':
                return renderJoinScreen();
            case 'configure':
                return renderConfigureScreen();
            case 'arena':
                return renderArenaScreen();
            case 'private':
                return renderPrivateScreen();
            default:
                throw new Error(`❌ UNKNOWN SCREEN in LeagueSignUp render: ${screen}`);
        }
    };

    return (
        <div className="league-signup-page ljf-page">
            {renderHeader()}
            {renderBalanceErrorBanner()}
            {renderScreen()}
            {renderInviteModal()}
            {renderInsufficientFundsModal()}
        </div>
    );
}

export default LeagueSignUp;
